<template>
	<Row class="page-container-row">
		<Col
			:span="24"
			:offset="0"
			:xl="xlSize"
			:xxl="xxlSize"
		>
			<PageContainer>
				<div class="scrollable-x">
					<div class="flex-wrapper scrollable-x">
						<TypographyTitle :level="2" class="flex-grow">
							{{ $t('base-stations.bsListHeader') }}
						</TypographyTitle>
						<Space class="buttons-container" v-if="isBaseStationChangeAllowed" :direction="buttonsDirection">
							<UsedBaseStationsTooltip :workingCount="workingBaseStationsCount" :maxCount="maxBaseStationsCount">
								<Button class="width-100" type="primary" @click="onAddButtonClick" :disabled="!allowAddingNewBaseStations">
									<PlusCircleOutlined />{{ $t('base-stations.addButtonLabel') }}
								</Button>
							</UsedBaseStationsTooltip>
							<UsedBaseStationsTooltip :workingCount="workingBaseStationsCount" :maxCount="maxBaseStationsCount">
								<Button class="width-100" @click="onStrImportButtonClick" :disabled="!allowAddingNewBaseStations">
									<FileAddOutlined />{{ $t('base-stations.strImportButtonLabel') }}
								</Button>
							</UsedBaseStationsTooltip>
							<PaasLinkButton />

							<Tooltip :trigger="['hover']" placement="bottom" :title="$t('base-stations.downloadJsonButtonLabel')">
								<Button class="width-100" @click.prevent="onDownloadJson">
									<DownloadOutlined /> <span v-if="smallScreen">{{ $t('base-stations.downloadJsonButtonLabel') }}</span>
								</Button>
							</Tooltip>

							<UploadButton
								:label="$t('base-stations.uploadJsonButtonLabel')"
								action="conf_bridge/upload/start/base_stations/"
								buttonClass="width-100"
								:onSuccess="handleUploadSuccess"
							/>
							<ConfirmImportModal
								v-model="confirmImportModalIsVisible"
								mode="base_stations"
								:importData="importData"
								:onSuccess="onImportSuccess"
							/>
						</Space>
					</div>
					<TablePaginationToolbar
						ref="paginationToolbarRef"
						v-model:items="baseStations"
						:defaultPageSize="20"
					/>
					<Table
						class="base-stations-table"
						:columns="columns"
						:data-source="paginatedBaseStations"
						:pagination="false"
						:row-selection="rowSelection"
						:loading="isLoading"
					>
						<template #headerCell="{ column }">
							<template v-if="column.key === 'antDetails'">
								<Tooltip :trigger="['hover']" placement="top" :title="$t('base-stations.tableColumns.antDetailsTooltip')">
									{{ column.title }} <QuestionCircleOutlined />
								</Tooltip>
							</template>
						</template>
						<template #bodyCell="{ column, record }">
							<template v-if="column.key === 'status'">
								<Badge :status="record.statusData.badge" :text="$t('baseStationStatus.' + record.status)" /><br>
							</template>
							<template v-if="column.key === 'statusOnline'">
								<Badge :status="record.statusOnlineData.badge" :text="$t('baseStationStatus.' + record.status_online)" />
							</template>
							<template v-if="column.key === 'statusMerged'">
								<Badge :status="record.statusData.badge" :text="$t('baseStationStatus.' + record.status)" /><br>
								<Badge :status="record.statusOnlineData.badge" :text="$t('baseStationStatus.' + record.status_online)" /><br>
								<Badge
									v-if="['broken', 'no_data'].includes(record.status_online)"
									:status="record.statusLiveData.badge"
									:text="$t('baseStationStatus.' + record.status_live)"
								/>
							</template>
							<template v-else-if="column.key === 'mountpoints'">
								<router-link
									v-if="isBaseStationChangeAllowed"
									:to="{ name: 'base-stations-edit', params: { id: record.id } }"
									:data-testid="record.id === paginatedBaseStations[0]?.id ? 'first-displayed-base-station-edit' : undefined"
								>
									<MountpointDisplay :baseStation="record" />
								</router-link>
								<MountpointDisplay v-else :baseStation="record" />
							</template>
							<template v-if="column.key === 'antDetails'">
								<div>
									{{ $t('base-stations-edit.fields.antReferencePoint.options.' + record.ant_reference_point) }}
									/
									<TypographyText :type="record.ant ? null : 'secondary'">
										{{ record.ant || $t('common.emptyValue') }}
									</TypographyText>
								</div>
								<DetectableFieldDisplay
									:dbValue="record.ant_reference_point"
									:compareValue="record.ant_reference_point_detected"
									nullValue="NA"
									displayValuePrefix="base-stations-edit.fields.antReferencePoint.options"
									compareAlertKey="base-stations-edit.fields.antReferencePoint.conflict.streamMessage"
									dbLabelKey="base-stations-edit.fields.antReferencePoint.conflict.databaseValue"
									compareLabelKey="base-stations-edit.fields.antReferencePoint.conflict.streamValue"
								/>
								<DetectableFieldDisplay
									v-if="record.allow_ant_detection"
									:dbValue="record.ant"
									:compareValue="record.ant_detected"
									nullValue=""
									compareAlertKey="base-stations-edit.fields.ant.conflict.streamMessage"
									dbLabelKey="base-stations-edit.fields.ant.conflict.databaseValue"
									compareLabelKey="base-stations-edit.fields.ant.conflict.streamValue"
								/>
							</template>
							<template v-else-if="column.key === 'displayAddress'">
								<TypographyText code>{{ record.display_address }}</TypographyText>
							</template>
							<template v-else-if="column.key === 'actions'">
								<ChangeStatusActionBar v-if="isBaseStationChangeAllowed" :target="record" :store="baseStationsStore" mode="baseStation" />
							</template>
						</template>
					</Table>
				</div>
				<GroupOperationsPopover :baseStations="selectedBaseStations" :onApply="onApplyGroupOperation" />
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, computed } from 'vue';
import { TypographyTitle, Table, Button, Row, Col, Badge, Space, Tooltip, TypographyText } from 'ant-design-vue';
import { DownloadOutlined, PlusCircleOutlined, FileAddOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';

import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import TranslationUtils from '@/i18n/TranslationUtils';

import { useUserStore } from '@/stores/user';
import { useBaseStationsStore } from '@/stores/baseStations';

import { useSmallScreen } from '@/utils/useSmallScreen';
import { addBaseStationStatusData } from '@/utils/baseStationsUtils';

import UsedBaseStationsTooltip from '@/components/baseStations/UsedBaseStationsTooltip';
import GroupOperationsPopover from '@/components/baseStations/GroupOperationsPopover';
import DetectableFieldDisplay from '@/components/baseStations/DetectableFieldDisplay';
import ChangeStatusActionBar from '@/components/baseStations/ChangeStatusActionBar';
import MountpointDisplay from '@/components/baseStations/MountpointDisplay';

import PaasLinkButton from '@/components/paas/PaasLinkButton';

import ConfirmImportModal from '@/components/common/ConfirmImportModal';
import PageContainer from '@/components/common/PageContainer';
import TablePaginationToolbar from '@/components/common/TablePaginationToolbar';
import UploadButton from '@/components/common/UploadButton';


export default {
	components: {
		TypographyTitle, Button, Row, Col, Table, Badge, Space, Tooltip, TypographyText,
		UsedBaseStationsTooltip, GroupOperationsPopover,
		PageContainer, ChangeStatusActionBar, ConfirmImportModal, MountpointDisplay, UploadButton, DetectableFieldDisplay, PaasLinkButton, TablePaginationToolbar,
		PlusCircleOutlined, FileAddOutlined, DownloadOutlined, QuestionCircleOutlined,
	},
	setup() {
		const { t } = useI18n();

		const baseStationsStore = useBaseStationsStore();
		const router = useRouter();

		// import modal
		const confirmImportModalIsVisible = ref(false);
		const importData = ref({});
		const handleUploadSuccess = response => {
			confirmImportModalIsVisible.value = true;
			importData.value = response;
		}
		const onImportSuccess = () => {
			baseStationsStore.baseStationRefreshList();
		};

		// table
		const user = useUserStore();
		const isBaseStationChangeAllowed = computed(() => user.isBaseStationChangeAllowed);
		const columns = TranslationUtils.addTranslatedField(
			user.baseStationsPageColumns,
			'key', 'title',
			'base-stations.tableColumns',
		);
		const baseStations = computed(() => baseStationsStore.items.map(addBaseStationStatusData));
		const paginationToolbarRef = ref(null);

		const isLoading = computed(() => baseStationsStore.isLoading && baseStationsStore.items.length === 0);
		
		const selectedRowKeys = ref([]);
		const selectedBaseStations = computed(() => baseStations.value.filter(item => selectedRowKeys.value.includes(item.id)));
		const onSelect = keys => selectedRowKeys.value = keys;
		const onApplyGroupOperation = () => selectedRowKeys.value = [];
		const rowSelection = computed(() => user.allowBaseStationsPageFull ? {
			selectedRowKeys: selectedRowKeys.value,
			onChange: onSelect,
			preserveSelectedRowKeys: true,
			selections: [{
				key: 'none',
				text: t('controls.table.deselectAll'),
				onSelect: () => {
					selectedRowKeys.value = [];
				},
			}],
		} : null);

		// layout
		const { smallScreen } = useSmallScreen();
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');
		const xlSize = computed(() => user.allowBaseStationsPageFull ? { span: 24, offset: 0 } : { span: 18, offset: 3 });
		const xxlSize = computed(() => user.allowBaseStationsPageFull ? { span: 24, offset: 0 } : { span: 12, offset: 6 });

		// add buttons
		const maxBaseStationsCount = computed(() => baseStationsStore.maxBaseStationsCount);
		const workingBaseStationsCount = computed(() => baseStationsStore.items.filter(item => ['working', 'updating'].includes(item.status)).length);
		const allowAddingNewBaseStations = computed(() => {
			return baseStationsStore.maxBaseStationsCount == null || baseStationsStore.maxBaseStationsCount > workingBaseStationsCount.value;
		});

		const onAddButtonClick = () => router.push({ name: 'add-base-station' });
		const onStrImportButtonClick = () => router.push({ name: 'str-import' });
		const onDownloadJson = () => Api.confBridgeDownloadJson();

		const paginatedBaseStations = computed(() => {
			return (paginationToolbarRef.value != null) ? paginationToolbarRef.value.paginatedItems : [];
		});
		return {
			baseStationsStore,
			confirmImportModalIsVisible,
			importData,
			handleUploadSuccess,
			onImportSuccess,

			paginationToolbarRef,
	
			columns,
			baseStations,
			paginatedBaseStations,
			isBaseStationChangeAllowed,
			isLoading,
			selectedRowKeys,
			rowSelection,
			selectedBaseStations,
			onSelect,
			onApplyGroupOperation,

			allowAddingNewBaseStations,
			maxBaseStationsCount,
			workingBaseStationsCount,

			onAddButtonClick,
			onStrImportButtonClick,
			onDownloadJson,

			smallScreen,
			buttonsDirection,
			xlSize,
			xxlSize,
			colorSuccess: Consts.COLORS.success,
			colorSuccessDark: Consts.COLORS.successDark,
		};
	},
}
</script>

<style>
.ant-upload {
	width: 100%;
	display: block;
}

.base-stations-table .ant-typography code {
	display: inline-block;
}
</style>

<style scoped>
.new-paas-results {
	background-color: v-bind(colorSuccessDark);
	border-color: v-bind(colorSuccessDark);
}

.new-paas-results:hover,
.new-paas-results:focus {
	background-color: v-bind(colorSuccess);
	border-color: v-bind(colorSuccess);
}

.buttons-container {
	margin-bottom: 16px;
}

.table-toolbar-row {
	display: flex;
	align-items: center;
	margin-bottom: 16px;
}
</style>
